// @ts-strict-ignore
import React, { ChangeEvent, useContext, useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import isEmpty from 'lodash-es/isEmpty'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Box as Form, Input, toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { usePutWalletDescriptionMutation } from 'hooks/mutations/usePutWalletDescriptionMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BaseErrorResponse } from 'types/beneficiaries'
import walletEditSchema from '../../schemas/walletEditSchema'

export interface WalletEditFormFields {
  walletName: string
}

export const WalletEditForm = () => {
  const { t } = useTranslation()
  const [walletAction] = useContext(WalletActionContext)
  const sourceWallet = walletAction?.meta?.sourceWallet
  const customerWallet = walletAction?.meta?.customerWallet

  const walletName = sourceWallet?.description ?? customerWallet?.name ?? ''
  const walletId = sourceWallet?.id ?? customerWallet?.id ?? undefined

  const queryClient = useQueryClient()
  const { mutate, isPending } = usePutWalletDescriptionMutation()

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const formSchema = walletEditSchema(t)

  const form = useForm<WalletEditFormFields>({
    defaultValues: {
      walletName: '',
    },
    resolver: yupResolver(formSchema),
  })

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = form

  const formValues = form.watch()

  useEffect(() => {
    setValue('walletName', walletName || '')
  }, [walletName])

  const onSubmit: SubmitHandler<typeof formValues> = async () => {
    resetRequestError()

    const payload = {
      walletId: walletId,
      description: formValues?.walletName,
    }

    mutate(payload, {
      onSuccess: async () => {
        toast({ header: t('walletNameUpdated'), variant: 'success' })

        queryClient.invalidateQueries({ queryKey: ['walletsV2'] })
        walletAction?.onClose()
      },
      onError: (error: BaseErrorResponse) => {
        setRequestError({
          show: true,
          errorCode: error?.status,
        })
        logSentryError('Error: usePutWalletDescriptionMutation', error)
      },
    })
  }

  return (
    <Form
      tag="form"
      width="full"
      flexCol
      gapY={16}
      onSubmit={handleSubmit(onSubmit)}
      testId="edit-wallet-name-form"
    >
      <Box flexCol>
        <Input
          {...register('walletName')}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setValue('walletName', e?.target?.value, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          label={t('walletName')}
          inputMode="text"
          name="walletName"
          testId="edit-wallet-name"
          value={formValues?.walletName}
          error={!!errors?.walletName}
          errorText={errors?.walletName?.message}
        />
      </Box>
      {requestError?.show && <RequestErrorCallout requestError={requestError} />}

      <Box flex justify="between" paddingT={16}>
        <Button testId="back" variant="secondary" onClick={walletAction?.onClose}>
          {t('cancel')}
        </Button>

        <Button
          testId="complete-payment"
          type="submit"
          variant="primary"
          loading={isPending}
          disabled={!isEmpty(errors)}
        >
          {t('save')}
        </Button>
      </Box>
    </Form>
  )
}
